import { makeEnum, makeOptions,
  setupClass, lookupData, 
} from '../Base.js'
import { generateItemName, sortItemsByName, } from '../SharedUtils.js'

export { Units } from '../Common/Units.js'

import {
  FieldType,
  Field,
  FieldGroup,
} from '../Common/Field.js'

import { gApp, DebugOn } from '../Globals.js'

export let InteriorShadeType = makeEnum({
  FabricCurtain: 'Fabric Curtain',
  LouveredShades: 'Louvered Shades',
  RollerShades: 'Roller Shades',
})

export let CurtainOpenness = makeEnum({
  Open: 'Open',
  SemiOpen: 'Semi-open',
  Closed: 'Closed',
})

export let CurtainColour = makeEnum({
  Light: 'Light',
  Medium: 'Medium',
  Dark: 'Dark',
})

export let SlatsLocation = makeEnum({
  IndoorSide: 'Indoor side',
  BetweenGlazing: 'Between glazing',
  OutdoorSide: 'Outdoor side',
})

export let ShadeReflectiveness = makeEnum({
  R0_15: '0.15',
  R0_5: '0.5',
  R0_8: '0.8',
})

export let RollerShadesDetail = makeEnum({
  Opaque: 'Opaque',
  Translucent: 'Translucent',
})

export let RollerShadesColour = makeEnum({
  Light: 'Light',
  LightGrey: 'Light grey',
  DarkGrey: 'Dark grey',
  ReflectiveWhite: 'Reflective white',
})

export let ShadesOrientation = makeEnum({
  Horizontal: 'Horizontal',
  Vertical: 'Vertical',
})

export let LouverAngle = makeEnum({
  Unknown: 'Unknown',
  ZeroDeg: '0°',
  TrackSolarAngle: 'Track solar angle',
  FortyFiveDeg: '45°',
  FullyClosed: 'Fully closed',
})

export class InteriorShadingType {
  init(name, makeId) {
    this.id = makeId ? gApp.proj().makeId('InteriorShadingType') : 0;

    this.name = Field.makeName('Name', name)
    this.type = new Field({
      name: 'Type',
      type: FieldType.Select,
      choices: makeOptions(InteriorShadeType),
      bold: true,
      note: 'The kind of interior shading. The remaining fields depend on this selection.',
    })

    this.fabricCurtainGroup = new FieldGroup([
      new Field({
        key: 'openness',
        name: 'Openness',
        type: FieldType.Select,
        choices: makeOptions(CurtainOpenness),
        note: 'How open the curtain weave is. More open weaves admit more solar gain.',
      }),
      new Field({
        key: 'colour',
        name: 'Colour',
        type: FieldType.Select,
        choices: makeOptions(CurtainColour),
        note: 'The colour of the curtain. Darker colours absorb more solar energy.',
      }),
    ])
    this.fabricCurtainGroup.setVisibility(() => {
      return this.type.value == InteriorShadeType.FabricCurtain;
    })

    this.louveredShadesGroup = new FieldGroup([
      new Field({
        key: 'location',
        name: 'Location',
        type: FieldType.Select,
        choices: makeOptions(SlatsLocation),
        note: 'Where the louvers are positioned relative to the glazing.',
      }),
      new Field({
        key: 'reflectiveness',
        name: 'Reflectiveness',
        type: FieldType.Select,
        choices: makeOptions(ShadeReflectiveness),
        note: 'The reflectance of the louver slats. Higher reflectance reduces solar gain.',
      }),
      Field.makeSelect('Louver angle', LouverAngle, {
        key: 'louverAngle',
        note: 'The angle of the louver slats. Select "Track solar angle" if the louvers are adjusted throughout the day, or "Unknown" if not known.',
      }),
    ])
    this.louveredShadesGroup.setVisibility(() => {
      return this.type.value == InteriorShadeType.LouveredShades;
    })

    this.rollerShadesGroup = new FieldGroup([
      new Field({
        key: 'detail',
        name: 'Visibility',
        type: FieldType.Select,
        choices: makeOptions(RollerShadesDetail),
        note: 'Whether the shade material is opaque (blocks all light) or translucent (admits diffused light).',
      }),
      new Field({
        key: 'colour',
        name: 'Colour',
        type: FieldType.Select,
        choices: makeOptions(RollerShadesColour),
        note: 'The colour of the roller shade. Lighter and reflective colours reduce solar gain.',
      })
    ])
    this.rollerShadesGroup.setVisibility(() => {
      return this.type.value == InteriorShadeType.RollerShades;
    })

    this.orientation = new Field({
      name: 'Orientation',
      type: FieldType.Select,
      choices: makeOptions(ShadesOrientation, [
        ShadesOrientation.Horizontal,
        ShadesOrientation.Vertical,
      ]),
      note: 'Whether the shading runs horizontally or vertically across the window.',
    })

    this.serFields = [
      'id',
      'name',
      'type',
      'fabricCurtainGroup',
      'louveredShadesGroup',
      'rollerShadesGroup',
      'orientation',
    ];
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Interior Shading',
      uniquePath: true,
      path: () => {
        return `internalshadings/${this.id}`
      }
    }
  }

  static getTypeMetadata() {
    return {
      name: 'Interior Shading',
      createType: InteriorShadingType.createType,
      openEditor: InteriorShadingType.openEditor,
      customEditor: true,
    }
  }

  static createType() {
    let type = InteriorShadingType.create(generateItemName('Interior Shading', gApp.proj().interiorShadingTypes), true)
    gApp.proj().interiorShadingTypes.push(type);
    sortItemsByName(gApp.proj().interiorShadingTypes);
    return type
  }

  static openEditor(type) {
    let pathName = gApp.proj().isCommercial() ? 'building-internalshadings' : 'internalshadings';
    gApp.proj().editorState.selectedInteriorShading = type;
    gApp.router.push({name: pathName});
  }

  static getTableInfo() {
    return {
      typeName: 'Interior Shading',
      pagePath: 'internalshadings',
      newItemName: 'InShading-$CTR',
      allowDuplicate: true,
      columns: {
        'name': {
          label: 'Name',
        },
        'style': {
          label: 'Style',
        },
        'details': {
          label: 'Details',
        },
        'orientation': {
          label: 'Orientation',
        },
      }
    }
  }

  getTableData() {
    let details = this.getDetails();
    return {
      name: this.name.value,
      style: InteriorShadeType._labels[this.type.value],
      details: `${details.detail1}, ${details.detail2}`,
      orientation: ShadesOrientation._labels[this.orientation.value],
    }
  }

  getInputPage() {
    return {
      label: `${this.name.value}`,
      // TODO
      path: `internalshadings/${this.id}`,
      typeName: 'Interior Shading',
      typeObject: this,
    };
  }

  getDetailsStr() {
    let details = this.getDetails();
    return `${details.detail1}, ${details.detail2}`
  }

  getDetails() {
    if (this.type.value == InteriorShadeType.FabricCurtain) {
      return {
        detail1: CurtainOpenness._labels[this.fabricCurtainGroup.getField('openness').value],
        detail2: CurtainColour._labels[this.fabricCurtainGroup.getField('colour').value],
      }
    } else if (this.type.value == InteriorShadeType.LouveredShades) {
      return {
        detail1: SlatsLocation._labels[this.louveredShadesGroup.getField('location').value],
        detail2: ShadeReflectiveness._labels[this.louveredShadesGroup.getField('reflectiveness').value],
      }
    } else if (this.type.value == InteriorShadeType.RollerShades) {
      return {
        detail1: RollerShadesDetail._labels[this.rollerShadesGroup.getField('detail').value],
        detail2: RollerShadesColour._labels[this.rollerShadesGroup.getField('colour').value],
      }
    } else {
      return {
        detail1: 'N/A',
        detail2: 'N/A',
      }
    }
  }
};
setupClass(InteriorShadingType)
